import type { ReactElement } from "react";
import { Boxes } from "lucide-react";
import { Badge, EmptyState, Stack, Text } from "@ux/primitives";
import { DataTable, type DataColumn, type DataTableSelection } from "@ux/compositions";
import type { SageOfficeView } from "../sage-offices/types";
import type { AssetView } from "./types";

export interface AssetsListProps {
    readonly rows: readonly AssetView[];
    /**
     * Office id → view map. Parent page resolves this once via
     * `useSageOffices` so we render the friendly office name in the
     * secondary line instead of the raw FK. Mirrors `WorkspacesList`.
     */
    readonly officeIndex: ReadonlyMap<string, SageOfficeView>;
    readonly selectedId: string | null;
    readonly onSelect: (row: AssetView) => void;
    readonly isLoading: boolean;
    readonly error: string | null;
    readonly selection?: DataTableSelection;
}

export function AssetsList({
    rows,
    officeIndex,
    selectedId,
    onSelect,
    isLoading,
    error,
    selection,
}: AssetsListProps): ReactElement {
    const columns: readonly DataColumn<AssetView>[] = [
        {
            id: "tag",
            header: "Asset",
            render: (row) => {
                const hasTag =
                    row.assetTag !== null && row.assetTag !== undefined && row.assetTag !== "";
                const primary = hasTag ? row.assetTag : `${row.make} ${row.model}`;
                // `sageOfficeId` is nullable on AssetView for the
                // legacy-rows-without-an-office case (see assetViewSchema
                // for the rationale). When null, surface a "No office"
                // badge so the row is visually flagged and easy to spot
                // in a long list — the operator can click in and pick
                // an office to migrate the record forward. Resolved
                // office name continues to render on the muted line
                // when the FK is populated and resolvable.
                const office =
                    row.sageOfficeId !== null ? officeIndex.get(row.sageOfficeId) : undefined;
                const detail = hasTag
                    ? `${row.type} · ${row.make} ${row.model}`
                    : `${row.type} · SN ${row.serialNumber}`;
                return (
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">
                            {primary}
                        </Text>
                        <Stack direction="row" gap={2} align="center" wrap>
                            <Text role="muted">{detail}</Text>
                            {row.sageOfficeId === null ? (
                                <Badge tone="warn">No office</Badge>
                            ) : (
                                <Text role="muted">
                                    · {office !== undefined ? office.name : row.sageOfficeId}
                                </Text>
                            )}
                        </Stack>
                    </Stack>
                );
            },
        },
        {
            id: "status",
            header: "Status",
            width: "150px",
            render: (row) =>
                row.isDeleted ? (
                    <Badge tone="danger">Deleted</Badge>
                ) : row.currentAssignmentId !== null && row.currentAssignmentId !== undefined ? (
                    <Badge tone="accent">Assigned</Badge>
                ) : (
                    <Badge tone="ok">Available</Badge>
                ),
        },
    ];

    if (isLoading && rows.length === 0) {
        return <EmptyState icon={<Boxes size={28} />} title="Loading assets…" />;
    }
    if (error !== null) {
        return (
            <EmptyState icon={<Boxes size={28} />} title="Could not load assets" description={error} />
        );
    }

    return (
        <DataTable<AssetView>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            selectedKey={selectedId}
            onSelect={onSelect}
            isRowMuted={(row) => row.isDeleted}
            {...(selection !== undefined ? { selection } : {})}
            emptyContent={
                <EmptyState
                    icon={<Boxes size={28} />}
                    title="No assets yet"
                    description="Add your first asset from the toolbar."
                />
            }
        />
    );
}
